<template>
  <div class="navigator-container">
    <div class="navigator-content">
      <b-button
        @click="previousStep"
        :disabled="currentStepIndex === 0 || submitted"
        outlined
      >
        {{ $t("af:preliminary.navigator.button.back") }}
      </b-button>

      <div class="progress-bar" v-if="loaded">
        <p class="is-size-7 has-text-centered">
          <span class="has-text-weight-medium"
            >{{ currentStepIndex + 1 }}/{{ totalSteps }}
            {{ $t("af:preliminary.navigator.label.step") }}</span
          >
          <span class="has-text-grey-icon ml-1"
            >({{ stepTime(currentStepIndex) }}
            {{ $t("af:preliminary.navigator.label.minute") }})</span
          >
        </p>
        <progress
          class="progress is-small"
          :value="progress"
          max="100"
        ></progress>
      </div>

      <div class="progress-bar is-flex is-flex-direction-column" v-else>
        <p class="has-text-centered ml-auto mr-auto mb-2">
          <b-skeleton height="12px" width="128px" />
        </p>
        <b-skeleton height="12px" />
      </div>

      <b-button
        v-if="currentStepIndex + 1 !== totalSteps || isTypeSelectStep"
        id="prelimButtonNext"
        class="next-btn"
        type="is-blue"
        @click="nextStep"
        :disabled="!isPsd2Synced"
        >{{ $t("af:preliminary.navigator.button.next") }}</b-button
      >
      <b-button
        v-if="currentStepIndex + 1 === totalSteps && !isTypeSelectStep"
        id="prelimButtonCalculation"
        class="next-btn"
        type="is-blue"
        @click="submit"
        :disabled="submitted || !isPsd2Synced"
        >{{ $t("af:preliminary.navigator.button.calculation") }}</b-button
      >
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import i18n from "@/plugins/i18n";
import { PRELIM_FIXED_STEPS, PRELIM_PSD2_FIXED_STEPS } from "@/utils/const";
import eventBus, { EVENTS } from "@/utils/eventBus";
import LeasingEmailConfirmationModal from "@/components/LeasingEmailConfirmationModal";

export default {
  name: "LeasingPrescoreNavigator",
  data() {
    return {
      loaded: true,
      PRELIM_FIXED_STEPS,
      submitted: false,
    };
  },
  computed: {
    ...mapGetters({
      steps: "leasingPrescore/steps",
      currentStepIndex: "leasingPrescore/currentStepIndex",
      stepTime: "leasingPrescore/stepTime",
      fixedSteps: "leasingPrescore/fixedSteps",
      isPsd2: "leasingPrescore/isPsd2",
      psd2Sync: "psd2/status",
    }),
    totalSteps() {
      return this.steps.length + this.fixedSteps; // including the first loan amount and term hard coded step
    },
    progress() {
      return ((this.currentStepIndex + 1) / this.totalSteps) * 100;
    },
    isPsd2Synced() {
      if (this.currentStepIndex === PRELIM_PSD2_FIXED_STEPS.SYNC) {
        if (this.isPsd2) {
          return (
            this.psd2Sync &&
            this.psd2Sync.hasSyncedBefore &&
            this.psd2Sync.lastSyncStatus === "SUCCESS"
          );
        }
      }
      return true;
    },
    isTypeSelectStep() {
      return this.currentStepIndex === PRELIM_FIXED_STEPS.TYPE_SELECT;
    },
  },
  created() {
    eventBus.$on(EVENTS.PRELIM_TYPE_CHANGED, this.onPrelimTypeChanged);
  },
  destroyed() {
    eventBus.$off(EVENTS.PRELIM_TYPE_CHANGED);
  },
  methods: {
    ...mapActions({
      updateStepIndex: "leasingPrescore/updateStepIndex",
      updatePrescore: "leasingPrescore/updatePrescore",
    }),
    ...mapMutations({
      nextStepMut: "leasingPrescore/nextStep",
      previousStep: "leasingPrescore/previousStep",
    }),
    storeData(questionId, value) {
      this.$store.dispatch("leasingPrescore/addData", {
        question: questionId,
        answer: value,
      });
    },
    nextStep() {
      this.checkStep(async () => {
        this.nextStepMut();
      });
    },
    async submit() {
      this.track();
      try {
        if (this.$store.getters["leasingPrescore/getType"] === "PSD2") {
          await this.$store.dispatch("psd2/getStatus", {});
          this.storeData("income", "" + this.psd2Sync.lastSyncData.salary);
        }
      } catch {}
      await this.updatePrescore();
      this.checkStep(async () => {
        this.submitted = true;
        let { success, redirectUrl } = await this.$store.dispatch(
          "leasingPrescore/submit"
        );
        await this.$store.dispatch("profile/updateStatus", {
          status: "LEASING_PRESCORE",
          result: success ? "SUCCESS" : "RETRY",
        });
        // await this.$store.dispatch("leasingPrescore/backToFirstStep");
        window.location.replace(redirectUrl);
      });
    },
    async checkStep(callback) {
      if (this.isPsd2 && this.isTypeSelectStep) {
        let isEmailConfirmedResponse = await this.$store.dispatch(
          "profile/isEmailConfirmed"
        );
        let isEmailConfirmed = isEmailConfirmedResponse.isEmailConfirmed;
        let currentEmail = isEmailConfirmedResponse.currentEmail;

        if (!isEmailConfirmed) {
          this.$buefy.modal.open({
            parent: this,
            component: LeasingEmailConfirmationModal,
            hasModalCard: true,
            trapFocus: true,
            canCancel: false,
            props: {
              currentEmail: currentEmail,
            },
          });
          return;
        }
      }

      if (
        this.$store.getters["leasingPrescore/checkStep"](this.currentStepIndex)
      ) {
        callback();
      } else {
        this.$buefy.toast.open({
          duration: 3000,
          message: i18n.t(
            "af:preliminary.message.validator.all_fields_required"
          ),
          position: "is-bottom",
          type: "is-danger",
        });
      }
    },
    onPrelimTypeChanged(started) {
      this.loaded = !started;
    },
    track() {
      let data = this.$store.state.preliminaryAssessment.data;
      this.$gtag.query("set", "user_properties", data);
      this.$gtag.event("preliminary_assessment");
    },
  },
  watch: {
    currentStepIndex: {
      handler(stepIndex) {
        this.updatePrescore({ step: stepIndex });
      },
      // immediate: true,
    },
  },
};
</script>
